import { useState } from "react";
import { ExternalLink, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { cn } from "@/shared/lib/utils";

import type { SiteCard, SiteKpis } from "../../api/types";
import { domainStatusText, kpiNumber, monthDayTime, previewHost } from "../../model/siteLabels";
import { siteStatusLabel } from "../../model/flowLabels";
import { isLaunched, sitePath, stepOf, stepPath } from "../../model/siteStep";
import { NewsStatusEntry } from "./flow/NewsStatusEntry";

/**
 * 屏 1 一张站点卡。两种形态:
 *  - 已发布:缩略图 + 域名两行 + KPI 四格 + 预览调整 / 上线设置(1010;编辑内容 / 域名 / 新闻栏进站点页「高级编辑」)/ 查看站点 / 回滚到上一版
 *    (按钮不写版本号:真正的目标是当前之下最近一个 published 版本,由列表页算出并写进确认框)/ 删除站点(1017,MANAGER+:
 *    宿主没有整站下线口,删除 = 立即下线,确认框由列表页写清);
 *  - 草稿:缩略图 + 进度一句话 + 开始生成 / 继续调整(按三段派生)/ 预览(发过预览才可点)/ 删除草稿。
 *  - 1010:卡上加「新闻自动更新」入口(未开启 → 去开启 / 已开启 · 每天 1 篇 → 管理);已上线卡加「预览调整」「上线设置」。
 *
 * ★ KPI 三项(访客 / AI 客服会话 / 留资线索)后端此刻全 null:**永远不画成 0**(0 是一个数,会被当成真的)。
 *   1009 C 线起不再占三格写「尚未接入」,而是合成一行「访客统计 · AI 客服 · 留资 —— 即将上线」(功能确实在做,
 *   一行带过比直接藏掉更不误导;三格空着像坏了)。哪项后端给了数,那项自动回到格子里。
 * ★ `publisherMode === "stub"` 的卡带「模拟发布」:发布服务器没接,站不是真上线的,卡上得说;主域名那一行也不说
 *   「HTTPS 已生效」(附录 A.38:模拟发布下不出现证书 / HTTPS 字样)。
 * ★ 缩略图不请求预览截图(没有这口),只用渐变 + 站点名占位。
 * ★ `archived` 是只读卡:「已归档」标 + 域名 / 版本信息保留,**不给任何按钮**(归档站不能编辑、
 *   不能回滚、也不该再点「查看站点」—— 网关上它已经下线)。
 * ★ host 模式(`publisherMode === "host"`,A.57):按钮叫「打开站点」= `previewUrl`(宿主 `?siteId=`);预览那一行写「宿主预览地址」。
 *   没发布过的站仍是「发布后可预览」禁用态(草稿卡的 publisherMode 是 null,本来就认不出 host,两种模式同一个说法)。
 * ★ STAFF 看不到「回滚到上一版」(A.59,`canManage=false`)。
 */
export function SiteCardView({
  site,
  busy,
  canManage = true,
  onRollback,
  onDelete,
  thumbnailUrl,
}: {
  site: SiteCard;
  /** 该站模板的缩略图(模板列表 thumbnailUrl);有预览 / 上线地址时用它兜底,以后有站点截图再换。 */
  thumbnailUrl?: string | null;
  /** TENANT_MANAGER+ 才有回滚(A.59)与已上线站的「删除站点」。 */
  canManage?: boolean;
  /** 这张卡上有写口在跑(回滚 / 删除),按钮上锁。 */
  busy: boolean;
  onRollback: (site: SiteCard) => void;
  onDelete: (site: SiteCard) => void;
}) {
  const isArchived = site.status === "archived";
  // 1010:「上线」按 launched 判断(只发了预览的站 launched=false,仍按草稿卡画:继续调整 / 预览 / 删除)
  const isDraft = !isArchived && !isLaunched(site);
  const statusLabel = siteStatusLabel(site);
  const isHost = site.publisherMode === "host";
  return (
    <article
      aria-label={site.name}
      className={cn(
        "overflow-hidden border border-border bg-card text-card-foreground shadow-sm",
        CARD_WALL_RADIUS
      )}
    >
      <div className="grid gap-0 md:grid-cols-[220px_minmax(0,1fr)]">
        <Thumb site={site} thumbnailUrl={thumbnailUrl} />
        <div className="flex min-w-0 flex-col gap-4 p-4">
          <header className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate text-base font-semibold text-foreground">{site.name}</h3>
                <Badge
                  variant={isDraft ? "outline" : "secondary"}
                  className="normal-case tracking-normal"
                >
                  {statusLabel}
                </Badge>
                {site.publisherMode === "stub" && (
                  <Badge
                    variant="outline"
                    title="发布服务器未接入"
                    className="border-warning/60 normal-case tracking-normal text-warning"
                  >
                    模拟发布
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{site.templateName}</p>
            </div>
          </header>

          {isDraft ? <DraftLine site={site} /> : <DomainLines site={site} />}

          {!isArchived && <NewsStatusEntry siteId={site.id} status={site.news} />}

          {!isDraft && !isArchived && <KpiRow site={site} />}

          {!isArchived && (
            <footer className="mt-auto flex flex-wrap items-center gap-2">
              {isDraft ? (
                <>
                  <Button asChild size="sm">
                    <Link to={sitePath(site.id, stepPath(site))} state={{ from: "/ai-website" }}>
                      {stepOf(site) === 1 ? "开始生成" : "继续调整"}
                    </Link>
                  </Button>
                  {site.currentVersionNo != null ? (
                    <Button asChild size="sm" variant="outline">
                      <a href={site.previewUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink />
                        预览
                      </a>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled title="生成后可预览">
                      预览
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={busy}
                    onClick={() => onDelete(site)}
                  >
                    {busy ? <Loader2 className="animate-spin" /> : <Trash2 />}
                    删除草稿
                  </Button>
                </>
              ) : (
                <>
                  <Button asChild size="sm">
                    <Link to={sitePath(site.id, "preview")} state={{ from: "/ai-website" }}>
                      预览调整
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link to={sitePath(site.id, "launch")} state={{ from: "/ai-website" }}>
                      上线设置
                    </Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <a href={site.previewUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink />
                      {isHost ? "打开站点" : "查看站点"}
                    </a>
                  </Button>
                  {canManage && site.currentVersionNo != null && site.currentVersionNo > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => onRollback(site)}
                    >
                      {busy ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                      回滚到上一版
                    </Button>
                  )}
                  {canManage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      disabled={busy}
                      onClick={() => onDelete(site)}
                    >
                      {busy ? <Loader2 className="animate-spin" /> : <Trash2 />}
                      删除站点
                    </Button>
                  )}
                </>
              )}
            </footer>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * 缩略图:没有站点截图口;有预览 / 上线地址(有版本号)时用该站模板的缩略图兜底并叠一行模板名,
 * 否则 / 图加载失败画一块带站点名的渐变(别去编一屏假首页文案)。
 */
function Thumb({ site, thumbnailUrl }: { site: SiteCard; thumbnailUrl?: string | null }) {
  const [broken, setBroken] = useState(false);
  if (thumbnailUrl && site.currentVersionNo != null && !broken) {
    return (
      <div className="relative min-h-[140px] overflow-hidden bg-muted" data-site-thumb="template">
        <img
          src={thumbnailUrl}
          alt={`${site.templateName} 模板缩略图`}
          className="absolute inset-0 h-full w-full object-cover object-top"
          loading="lazy"
          onError={() => setBroken(true)}
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6">
          <div className="truncate text-2xs text-white/90">模板 · {site.templateName}</div>
        </div>
      </div>
    );
  }
  return (
    <div
      aria-hidden
      className="flex min-h-[140px] flex-col justify-end bg-gradient-to-br from-primary/15 via-primary/5 to-muted p-4"
    >
      <div className="text-2xs uppercase tracking-wider text-muted-foreground">
        {site.templateName}
      </div>
      <div className="truncate text-sm font-semibold text-foreground/80">{site.name}</div>
    </div>
  );
}

function DomainLines({ site }: { site: SiteCard }) {
  return (
    <dl className="space-y-1 text-xs">
      {site.primaryDomain ? (
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt className="font-medium text-foreground">{site.primaryDomain.domain}</dt>
          <dd className="text-muted-foreground">
            {domainStatusText(site.primaryDomain.overallStatus, site.publisherMode === "stub")}
          </dd>
        </div>
      ) : (
        <div className="text-muted-foreground">尚未绑定自有域名</div>
      )}
      <div className="flex flex-wrap items-baseline gap-x-2">
        <dt className="font-medium text-foreground">{previewHost(site.previewUrl)}</dt>
        <dd className="text-muted-foreground">
          {site.publisherMode === "host" ? "宿主预览地址" : "平台预览域名"}
        </dd>
      </div>
    </dl>
  );
}

/** 草稿卡那一句进度(1010 三段式):「待生成 / 预览调整中 · 尚未绑定域名(先用 …)」。 */
function DraftLine({ site }: { site: SiteCard }) {
  const parts = [
    stepOf(site) === 1 ? "待生成" : site.currentVersionNo != null ? "预览调整中" : "内容编辑中",
    site.primaryDomain
      ? site.primaryDomain.domain
      : `尚未绑定域名(发布后先用 ${previewHost(site.previewUrl)})`,
  ];
  return <p className="text-xs text-muted-foreground">{parts.join(" · ")}</p>;
}

const KPI_TILES: ReadonlyArray<{ key: keyof SiteKpis; label: string; short: string }> = [
  { key: "visitors", label: "近 7 天访客", short: "访客统计" },
  // 1014:建站舱官网已能自动挂客服浮窗(上线页一键接入);这里「即将上线」只指会话统计。
  { key: "sessions", label: "AI 客服会话", short: "客服会话统计" },
  { key: "leads", label: "留资线索", short: "留资" },
];

/** 统计口没接的那几项合成一行「访客统计 · AI 客服 · 留资 —— 即将上线」(1009 C 线:不占三格「尚未接入」)。 */
const KPI_COMING_SOON_SUFFIX = "即将上线";

function KpiRow({ site }: { site: SiteCard }) {
  // 契约里 kpis 非空,但统计口没接的后端可能整个不给 —— 当成全 null,别在卡上炸。
  const kpis: Partial<SiteKpis> = site.kpis ?? {};
  const publishedAt = monthDayTime(site.publishedAt);
  const live = KPI_TILES.filter(({ key }) => (kpis[key] ?? null) !== null);
  const soon = KPI_TILES.filter(({ key }) => (kpis[key] ?? null) === null);
  return (
    <div className="space-y-1.5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {live.map(({ key, label }) => (
          <div key={key} className="rounded-md border border-border/70 bg-muted/30 px-3 py-2">
            <div className="text-2xs text-muted-foreground">{label}</div>
            <div className="text-base font-semibold tabular-nums text-foreground">
              {kpiNumber(kpis[key] as number)}
            </div>
          </div>
        ))}
        <div className="rounded-md border border-border/70 bg-muted/30 px-3 py-2">
          <div className="text-2xs text-muted-foreground">当前版本</div>
          {site.currentVersionNo == null ? (
            <div className="text-xs text-muted-foreground/70">未发布</div>
          ) : (
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              <span className="text-base font-semibold tabular-nums text-foreground">
                v{site.currentVersionNo}
              </span>
              {publishedAt && (
                <span className="text-2xs text-muted-foreground">{publishedAt} 发布</span>
              )}
            </div>
          )}
        </div>
      </div>
      {soon.length > 0 && (
        <p className="text-2xs text-muted-foreground">
          {soon.map((k) => k.short).join(" · ")} —— {KPI_COMING_SOON_SUFFIX}
        </p>
      )}
    </div>
  );
}
